import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useBrand } from '../../Brand';
import { interpolate, useTranslation } from '../../i18n';
import { colors } from '../../theme/tokens';

/** Marca d'água da Nogle, fixa em todas as telas do produto. */
const NOGLE_BRAND_IMAGE =
  'https://s3-contel-imagens-aplicacao.s3.us-east-1.amazonaws.com/nogle/Brand+cinza.svg';

function WhatsAppIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  );
}

function GooglePlayIcon() {
  return (
    <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.3,12.5C20.69,12.28 20.69,11.72 20.3,11.5L17.89,10.11L15.39,12.61L17.89,15.11L20.3,12.5M16.81,8.88L14.54,11.15L6.05,2.66L16.81,8.88Z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.69C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.37 12.36,4.26 13,3.5Z" />
    </svg>
  );
}

interface FooterEntryProps {
  label: string;
  children: React.ReactNode;
}

function FooterEntry({ label, children }: FooterEntryProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography
        component="span"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          color: colors.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Rodapé compartilhado das telas do Recarga Fácil.
 *
 * O bloco de contato (SAC, WhatsApp, lojas de app e site) aparece sempre que a
 * identidade traz algum desses dados — inclusive na raiz, onde a API devolve o
 * bloco padrão Contel. A assinatura da Nogle e o aviso de direitos aparecem
 * sempre.
 */
export function BrandFooter() {
  const { identity } = useBrand();
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  // O bloco de contato aparece quando há o que mostrar. Na raiz `/` a API
  // devolve o bloco padrão Contel (SAC e site preenchidos), que é o que o
  // middleware Laravel mesclava no rodapé — por isso o critério é o conteúdo,
  // não a origem da marca.
  const hasContact = Boolean(
    identity.sac ||
      identity.whatsapp ||
      identity.playStoreUrl ||
      identity.appStoreUrl ||
      identity.site
  );

  const linkSx = {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: colors.primary,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    '&:hover': { textDecoration: 'underline' },
  };

  const storeLinkSx = {
    ...linkSx,
    color: colors.textSecondary,
    '&:hover': { color: colors.primary, textDecoration: 'none' },
  };

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        mt: 'auto',
        backgroundColor: colors.surface,
        borderTop: `1px solid ${colors.border}`,
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: 2, textAlign: 'center' }}>
        {hasContact ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 4,
              mb: 3,
            }}
          >
            {identity.sac ? (
              <FooterEntry label={t.footer.customerService}>
                <Link href={`tel:${identity.sac}`} sx={linkSx}>
                  {identity.sac}
                </Link>
              </FooterEntry>
            ) : null}

            {identity.whatsapp ? (
              <FooterEntry label={t.footer.whatsapp}>
                <Link
                  href={`https://api.whatsapp.com/send?phone=${identity.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={linkSx}
                >
                  <WhatsAppIcon />
                  {identity.whatsapp}
                </Link>
              </FooterEntry>
            ) : null}

            {identity.playStoreUrl ? (
              <FooterEntry label={t.footer.androidApp}>
                <Link
                  href={identity.playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={storeLinkSx}
                >
                  <GooglePlayIcon />
                  {t.footer.googlePlay}
                </Link>
              </FooterEntry>
            ) : null}

            {identity.appStoreUrl ? (
              <FooterEntry label={t.footer.iosApp}>
                <Link
                  href={identity.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={storeLinkSx}
                >
                  <AppleIcon />
                  {t.footer.appStore}
                </Link>
              </FooterEntry>
            ) : null}

            {identity.site ? (
              <FooterEntry label={t.footer.website}>
                <Link
                  href={
                    /^https?:\/\//i.test(identity.site) ? identity.site : `https://${identity.site}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ...storeLinkSx, fontWeight: 500 }}
                >
                  {identity.site}
                </Link>
              </FooterEntry>
            ) : null}
          </Box>
        ) : null}

        <Box
          sx={{
            borderTop: `1px solid ${colors.borderSoft}`,
            pt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Box
            component="img"
            src={NOGLE_BRAND_IMAGE}
            alt={t.footer.nogleAlt}
            sx={{ height: 24, opacity: 0.8 }}
          />
          <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
            {interpolate(t.footer.copyright, { year })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
